import { all, call, delay, fork, put, race, take, takeEvery, select } from "redux-saga/effects";

import * as JobActions from "../actions/jobAction";
import * as AppActions from "../actions/appAction";
import * as api from "../../api/inferenceJob";
import { showErrorMessage } from "./shared";
import { ICreateJobRequest, IJob, IJobAdditionalAttributes, IJobListTimestampRange, IJobRequest, IJobs, IQueueJobRequest, JobStatus, IProcessLocalJobRequest, IProcessLocalAveragingJobRequest } from "../../models";
import { leaveBreadcrumb, addMetadata } from "../../utils/tracker";
import { buildLocalReportContext } from "src/utils/localReportContext";

// Name of action which ends the polling
const CANCEL_STATUS_POLLING = "CancelJobStatusPolling"
const CANCEL_USER_JOBS_POLLING = "CancelUserJobsPolling"

import { IStore } from "../reducers";
import { RICE_GRAIN_ID } from "src/constants";
import {
  getAttemptsOnEmptyResults,
  getPageInterval,
  getPollingDelay,
  getJobListPollingDelay,
  getRefreshInterval
} from "src/utils/remoteConfig";

export const getGrainType = (store: IStore) => store.grainType.grainId;
export const getProfileId = (store: IStore) => store.grainProfile.profileId;
export const getSelectedProfileMapping = (store: IStore) => store.grainProfile.selectedGrainProfileMapping;
export const getBrokenParam = (store: IStore) => store.grainProfile.brokenParam;
export const getBrokenByShape = (store: IStore) => store.grainProfile.brokenByShape;
export const getCurrentJobId = (state: any) => state.job.jobId;

export const getUserJobsRange = (store: IStore) => store.job.userJobsRange;
export const getOrgJobsRange = (store: IStore) => store.job.orgJobsRange;

export const getUserJobsCachedRange = (store: IStore) => store.app.userJobsCachedRange;
export const getOrgJobsCachedRange = (store: IStore) => store.app.orgJobsCachedRange;

export const getDeviceId = (store: IStore) => store.notification.deviceId;
export const getUsername = (store: IStore) => store.user.userProfile.response?.username;
export const getOrgId = (store: IStore) => store.user.userProfile.response?.org?.orgId;
export const getOrg = (store: IStore) => store.user.userProfile.response?.org;
export const getCenterId = (store: IStore) => store.user.userProfile.response?.center?.centerId;
export const getCenter = (store: IStore) => store.user.userProfile.response?.center;
export const getSurveyor = (store: IStore) => store.user.userProfile.response?.surveyor;
export const getUserSettings = (store: IStore) => store.user.userProfile.response?.settings;
export const getScannerId = (store: IStore) => store.user.userProfile.response?.settings?.scanner_id;
export const getGrainProfiles = (store: IStore) => store.grainProfile.grainProfiles;
export const getSelectedGrainProfile = (store: IStore) => {
  const grainId = store.grainType.grainId;
  const profileId = store.grainProfile.profileId;
  return (store.grainProfile.grainProfiles || []).find(
    (profile) => `${profile.grainId}` === `${grainId}` && `${profile.profileId}` === `${profileId}`
  );
};
export const getJobRequest = (store: IStore) => store.job.jobRequest;
export const getNetworkStatus = (store: IStore) => store.app.isOnline;
export const getPreferredProcessingMode = (store: IStore) => store.settings.preferredProcessingMode;
export const getCommittedDraftInfoByJobId = (store: IStore) => store.job.committedDraftInfoByJobId;
export const getCurrentJob = (store: IStore) => store.job.jobStatus.job;

export function* createJob(action: ReturnType<typeof JobActions.createJob.request>): any {
  const request: ICreateJobRequest = action.payload;

  try {
    const isOnline: boolean = yield select(getNetworkStatus);
    if (!isOnline) {
      throw new Error('Internet connection required to create cloud jobs');
    }
    const grainId = yield select(getGrainType);
    const profileId = yield select(getProfileId);
    const selectedGrainProfile = yield select(getSelectedGrainProfile);
    const deviceId = yield select(getDeviceId);
    const storedJobRequest = yield select(getJobRequest);
    const committedDraftInfoByJobId = yield select(getCommittedDraftInfoByJobId);
    const committedInfo = committedDraftInfoByJobId?.[request.jobId];
    const center = yield select(getCenter);
    const scannerId = yield select(getScannerId);

    leaveBreadcrumb('Job creation started', {
      jobId: request.jobId,
      source: request.source,
      grainId: grainId,
      profileId: profileId,
      hasLocation: !!request.location
    }, 'process');

    let additionalAttributes: IJobAdditionalAttributes | undefined = undefined;

    if (`${grainId}` === RICE_GRAIN_ID) {
      const selectedProfileMapping = yield select(getSelectedProfileMapping);
      const brokenParam = yield select(getBrokenParam);
      const brokenByShape = yield select(getBrokenByShape);
      additionalAttributes = {
        rice_process: selectedProfileMapping.process,
        rice_size: selectedProfileMapping.size,
        // rice_color: selectedProfileMapping.color
        broken_param: brokenParam,
        broken_by_shape: brokenByShape
      }
    }

    if (scannerId) {
      additionalAttributes = {
        ...(additionalAttributes || {}),
        scannerId: `${scannerId}`
      };
    }

    const info: { [key: string]: string } = {
      ...(committedInfo || {}),
      location: request.location
    };
    if (center?.centerId) info.centerId = String(center.centerId);
    if (center?.name) info.centerName = String(center.name);
    if (center?.addressDistrict) info.centerDistrict = String(center.addressDistrict);
    if (center?.addressCity) info.centerCity = String(center.addressCity);

    const createJobRequest: IJobRequest = {
      jobId: request.jobId,
      grainId: grainId,
      profileId: profileId,
      object_key: request.object_key,
      deviceId: deviceId,
      additionalAttributes: additionalAttributes,
      source: request.source,
      info,
      results: request.manualEntryInput || undefined,
      selectedJobs: storedJobRequest?.selectedJobs || undefined
    }

    const response = yield call(api.createJob, createJobRequest);

    leaveBreadcrumb('Job created successfully', {
      jobId: request.jobId,
      source: request.source,
      grainId: grainId,
      isManualEntry: !!request.manualEntryInput,
      isAveraged: !!storedJobRequest?.selectedJobs
    }, 'process');

    addMetadata('currentJob', {
      jobId: request.jobId,
      grainId: grainId,
      profileId: profileId,
      source: request.source,
      createdAt: new Date().toISOString()
    });

    yield put(JobActions.createJob.success(response));

    // Trigger immediate refresh of user jobs list to show the newly created job
    const username = yield select(getUsername);
    if (username) {
      yield put(JobActions.userJobs.request({
        username: username,
        from: (new Date().getTime() / 1000) - 60, // Look back 60 seconds
        to: new Date().getTime() / 1000
      }));
    }
  } catch (error: any) {
    leaveBreadcrumb('Job creation failed', {
      jobId: request.jobId,
      source: request.source,
      error: error.message
    }, 'error');
    showErrorMessage(error, {
      flow: 'job_creation',
      jobId: request.jobId,
      source: request.source
    });
    yield put(JobActions.createJob.failure(error));
  } finally {
    // Ensure committed draft info doesn't grow unbounded.
    yield put(JobActions.clearCommittedDraftInfo(request.jobId));
  }
}

export function* queueJob(action: ReturnType<typeof JobActions.queueJob.request>): any {
  try {
    const request: IQueueJobRequest = action.payload;
    const response = yield call(api.queueJob, request);
    yield put(JobActions.queueJob.success(response));
  } catch (error: any) {
    showErrorMessage(error);
    yield put(JobActions.queueJob.failure(error));
  }
}

export function* updateJob(action: ReturnType<typeof JobActions.updateJob.request>): any {
  try {
    leaveBreadcrumb('Job update started', {
      jobId: action.payload.jobId
    }, 'process');

    const response = yield call(api.updateJob, action.payload);

    leaveBreadcrumb('Job updated successfully', {
      jobId: action.payload.jobId
    }, 'process');

    yield put(JobActions.updateJob.success(response));
    yield put(JobActions.pollJobStatus.request(action.payload.jobId));
  } catch (error: any) {
    leaveBreadcrumb('Job update failed', {
      jobId: action.payload.jobId,
      error: error.message
    }, 'error');
    showErrorMessage(error, {
      flow: 'job_update',
      jobId: action.payload.jobId
    });
    yield put(JobActions.updateJob.failure(error));
  }
}

export function* updateLocalJobInfo(action: ReturnType<typeof JobActions.updateLocalJobInfo.request>): any {
  try {
    const request = action.payload;
    const isOnline: boolean = yield select(getNetworkStatus);
    const currentJob: any = yield select(getCurrentJob);
    const wasSynced =
      currentJob?.jobId === request.jobId &&
      (currentJob?.syncStatus === 'synced' || !!currentJob?.syncedAt);

    if (typeof window === 'undefined' || !(window as any).electron?.updateLocalJobInfo) {
      throw new Error('Local job info updates are available only in the desktop app.');
    }

    const resp = yield call((window as any).electron.updateLocalJobInfo, {
      jobId: request.jobId,
      info: request.info
    });

    if (!resp?.success) {
      throw new Error(resp?.error || 'Failed to update local job info');
    }

    // Refresh job state in Redux (getJobUnified will hydrate from SQLite).
    yield put(JobActions.pollJobStatus.request(request.jobId));

    // If the job was previously synced to cloud, update the cloud job info via PUT.
    if (wasSynced && isOnline) {
      try {
        yield call(api.updateJob, request);
        if ((window as any).electron?.updateLocalJobSyncStatus) {
          yield call((window as any).electron.updateLocalJobSyncStatus, {
            jobId: request.jobId,
            syncStatus: 'synced',
            syncError: null,
            syncedAt: Math.floor(Date.now() / 1000)
          });
        }
      } catch (error: any) {
        if ((window as any).electron?.updateLocalJobSyncStatus) {
          yield call((window as any).electron.updateLocalJobSyncStatus, {
            jobId: request.jobId,
            syncStatus: 'failed',
            syncError: error?.message || 'Cloud update failed'
          });
        }
        throw error;
      }
    }

    yield put(JobActions.updateLocalJobInfo.success(true));
  } catch (error: any) {
    showErrorMessage(error, {
      flow: 'local_job_info_update',
      jobId: action.payload?.jobId
    });
    yield put(JobActions.updateLocalJobInfo.failure(error));
  }
}

export function* updateJobResult(action: ReturnType<typeof JobActions.updateJobResult.request>): any {
  try {
    const response = yield call(api.updateJobResult, action.payload);
    yield put(JobActions.updateJobResult.success(response));
  } catch (error: any) {
    showErrorMessage(error);
    yield put(JobActions.updateJobResult.failure(error));
  }
}

export function* processLocalJob(
  action: ReturnType<typeof JobActions.processLocalJob.request>
): any {
  try {
    const request: IProcessLocalJobRequest = action.payload;
    const grainId = request.grainId ?? (yield select(getGrainType));
    const profileId = request.profileId ?? (yield select(getProfileId));
    const selectedGrainProfile = yield select(getSelectedGrainProfile);
    const username = yield select(getUsername);
    const orgId = yield select(getOrgId);
    const centerId = yield select(getCenterId);
    const organization = yield select(getOrg);
    const center = yield select(getCenter);
    const surveyor = yield select(getSurveyor);
    const userSettings = yield select(getUserSettings);
    const scannerId = yield select(getScannerId);

    let additionalAttributes: IJobAdditionalAttributes | undefined = request.additionalAttributes;
    if (!additionalAttributes && `${grainId}` === RICE_GRAIN_ID) {
      const selectedProfileMapping = yield select(getSelectedProfileMapping);
      const brokenParam = yield select(getBrokenParam);
      const brokenByShape = yield select(getBrokenByShape);
      additionalAttributes = {
        rice_process: selectedProfileMapping.process,
        rice_size: selectedProfileMapping.size,
        broken_param: brokenParam,
        broken_by_shape: brokenByShape
      };
    }
    if (scannerId) {
      additionalAttributes = {
        ...(additionalAttributes || {}),
        scannerId: `${scannerId}`
      };
    }
    if (request.productName === 'DOUBLE_SIDED_GRAIN_ANALYSIS') {
      const attrs: any = additionalAttributes || { rice_process: '0', rice_size: '0' };
      attrs.scannerId = 'DS1';
      attrs.mlInferenceEnabled = true;
      attrs.inferenceMode = attrs.inferenceMode || 'local';
      additionalAttributes = attrs;
    }

    if (typeof window === 'undefined' || !(window as any).electron) {
      throw new Error('Electron IPC is not available');
    }

    // Windows Electron: ensure CPU ML runtime is installed before running local processing.
    // This is a hard guard so first local job doesn't fail even if the post-login background install is still running.
    try {
      const electronPlatform = (window as any)?.electron?.platform || null;
      const electronAPI = (window as any)?.electronAPI || null;
      if (electronPlatform === 'win32' && electronAPI?.ensureMlRuntimeInstalled) {
        const ensureResp = yield call([electronAPI, 'ensureMlRuntimeInstalled'], { kind: 'cpu', source: 'bundled' });
        if (!ensureResp?.success) {
          throw new Error(ensureResp?.error || 'CPU ML runtime is not available');
        }
      }
    } catch (error: any) {
      throw new Error(error?.message || 'CPU ML runtime is not available');
    }

    const reportContext = buildLocalReportContext({
      baseInfo: request.info ?? null,
      location: request.location,
      organization,
      center,
      surveyor,
      userSettings,
    });
    const info = reportContext.info;

    const nowEpochSeconds = Math.floor(Date.now() / 1000);
    yield put(
      JobActions.upsertOptimisticUserJob({
        jobId: request.jobId,
        grainId,
        profileId,
        jobStatus: JobStatus.JOB_PROCESSING_LOCAL,
        processingMode: 'local',
        createdAt: nowEpochSeconds,
        updatedAt: nowEpochSeconds,
        username,
        source: request.source,
        location: request.location,
        info,
        additionalAttributes,
        productName: request.productName
      })
    );

    const result = yield call((window as any).electron.processLocalJob, {
      jobId: request.jobId,
      grainId,
      profileId,
      additionalAttributes,
      source: request.source,
      location: request.location,
      info,
      organizationName: reportContext.organizationName,
      centerName: reportContext.centerName,
      organizationLogoKey: reportContext.organizationLogoKey,
      organizationLogoUrl: reportContext.organizationLogoUrl,
      reportLogoUrl: reportContext.reportLogoUrl,
      productName: request.productName,
      imagePath: request.imagePath,
      imagePaths: request.imagePaths,
      username,
      orgId,
      centerId,
      surveyor
    });

    if (result?.success) {
      const normalizedResults = result?.results?.results ? result.results.results : result.results;
      const job: IJob = {
        jobId: request.jobId,
        grainId,
        profileId,
        jobStatus: JobStatus.JOB_COMPLETED,
        processingMode: 'local',
        createdAt: Math.floor(Date.now() / 1000),
        updatedAt: Math.floor(Date.now() / 1000),
        results: normalizedResults,
        info,
        additionalAttributes,
        productName: request.productName
      };

      yield put(JobActions.processLocalJob.success(job));
      yield put(
        JobActions.updateRpiJobStatus({
          jobId: request.jobId,
          status: JobStatus.JOB_COMPLETED
        })
      );
    } else {
      throw new Error(result?.error || 'Local processing failed');
    }
  } catch (error: any) {
    try {
      const nowEpochSeconds = Math.floor(Date.now() / 1000);
      const grainId = action?.payload?.grainId ?? (yield select(getGrainType));
      const profileId = action?.payload?.profileId ?? (yield select(getProfileId));
      const username = yield select(getUsername);
      if (action?.payload?.jobId && grainId && profileId) {
        yield put(
          JobActions.upsertOptimisticUserJob({
            jobId: action.payload.jobId,
            grainId,
            profileId,
            jobStatus: JobStatus.JOB_FAILED,
            processingMode: 'local',
            createdAt: nowEpochSeconds,
            updatedAt: nowEpochSeconds,
            username,
            source: action.payload?.source,
            location: action.payload?.location,
            info: action.payload?.info ?? { location: action.payload?.location },
            additionalAttributes: action.payload?.additionalAttributes,
            productName: action.payload?.productName
          })
        );
      }
    } catch {
      // best-effort optimistic update only
    }
    yield put(JobActions.processLocalJob.failure(error));
    yield put(
      JobActions.updateRpiJobStatus({
        jobId: action.payload.jobId,
        status: JobStatus.JOB_FAILED
      })
    );
  }
}

export function* processLocalAveragingJob(
  action: ReturnType<typeof JobActions.processLocalAveragingJob.request>
): any {
  const request: IProcessLocalAveragingJobRequest = action.payload;

  try {
    if (typeof window === 'undefined' || !(window as any).electron?.processLocalAveragingJob) {
      throw new Error('Local averaging is available only in the desktop app.');
    }

    const username = yield select(getUsername);
    const orgId = yield select(getOrgId);
    const organization = yield select(getOrg);
    const center = yield select(getCenter);
    const surveyor = yield select(getSurveyor);
    const userSettings = yield select(getUserSettings);
    const reportContext = buildLocalReportContext({
      baseInfo: request.info ?? null,
      location: request.location,
      organization,
      center,
      surveyor,
      userSettings,
    });
    const info = reportContext.info;
    const nowEpochSeconds = Math.floor(Date.now() / 1000);
    const additionalAttributes: IJobAdditionalAttributes = {
      ...(request.additionalAttributes || {}),
      sourceJobIds: request.selectedJobs,
    };

    yield put(
      JobActions.upsertOptimisticUserJob({
        jobId: request.jobId,
        grainId: request.grainId,
        profileId: request.profileId,
        jobStatus: JobStatus.JOB_PROCESSING_LOCAL,
        processingMode: 'local',
        createdAt: nowEpochSeconds,
        updatedAt: nowEpochSeconds,
        username,
        source: 'averaging',
        location: request.location,
        info,
        additionalAttributes,
        productName: 'GRAIN_ANALYSIS_AVERAGING'
      })
    );

    const result = yield call((window as any).electron.processLocalAveragingJob, {
      jobId: request.jobId,
      selectedJobs: request.selectedJobs,
      grainId: request.grainId,
      profileId: request.profileId,
      additionalAttributes,
      info,
      source: 'averaging',
      location: request.location,
      organizationName: reportContext.organizationName,
      centerName: reportContext.centerName,
      username,
      orgId
    });

    if (!result?.success) {
      throw new Error(result?.error || 'Local averaging failed');
    }

    const normalizedResults = result?.results?.results ? result.results.results : result.results;
    const job: IJob = {
      jobId: request.jobId,
      grainId: request.grainId,
      profileId: request.profileId,
      jobStatus: JobStatus.JOB_COMPLETED,
      processingMode: 'local',
      createdAt: nowEpochSeconds,
      updatedAt: Math.floor(Date.now() / 1000),
      results: normalizedResults,
      info,
      additionalAttributes,
      productName: 'GRAIN_ANALYSIS_AVERAGING',
      source: 'averaging',
      location: request.location
    };

    yield put(JobActions.processLocalAveragingJob.success(job));
  } catch (error: any) {
    try {
      const nowEpochSeconds = Math.floor(Date.now() / 1000);
      const username = yield select(getUsername);
      if (request?.jobId && request?.grainId && request?.profileId) {
        yield put(
          JobActions.upsertOptimisticUserJob({
            jobId: request.jobId,
            grainId: request.grainId,
            profileId: request.profileId,
            jobStatus: JobStatus.JOB_FAILED,
            processingMode: 'local',
            createdAt: nowEpochSeconds,
            updatedAt: nowEpochSeconds,
            username,
            source: 'averaging',
            location: request.location,
            info: request.info ?? { location: request.location },
            additionalAttributes: {
              ...(request.additionalAttributes || {}),
              sourceJobIds: request.selectedJobs,
            },
            productName: 'GRAIN_ANALYSIS_AVERAGING'
          })
        );
      }
    } catch {
      // best-effort optimistic update only
    }
    showErrorMessage(error, {
      flow: 'local_averaging',
      jobId: request?.jobId
    });
    yield put(JobActions.processLocalAveragingJob.failure(error));
  }
}

export function* getJob(action: ReturnType<typeof JobActions.jobStatus.request>): any {
  try {
    const response = yield call(api.getJobUnified, action.payload);
    yield put(JobActions.jobStatus.success(response));
  } catch (error: any) {
    showErrorMessage(error);
    yield put(JobActions.jobStatus.failure(error));
  }
}

function* getPollingRange(lastRange: IJobListTimestampRange): any {

  var toTimestamp = new Date().getTime() / 1000;
  var fromTimestamp = (lastRange === undefined) ? ((new Date().getTime() / 1000) - getPageInterval()) :
    ((new Date().getTime() / 1000) - getRefreshInterval());
  return {
    'from': fromTimestamp,
    'to': toTimestamp
  }
}

function* getPaginationRange(lastRange: IJobListTimestampRange): any {

  var toTimestamp = (lastRange === undefined) ? (new Date().getTime() / 1000) : lastRange.from;
  var fromTimestamp = (lastRange === undefined) ? (new Date().getTime() / 1000) - getPageInterval() :
    (lastRange.from - getPageInterval());
  return {
    'from': fromTimestamp,
    'to': toTimestamp
  }
}

export function* getJobs(action: ReturnType<typeof JobActions.userJobs.request>): any {
  try {
    const request = action.payload
    if (request.from !== undefined && request.to !== undefined) {
      const response: IJobs = yield call(api.getUserJobsUnified, request);
      yield put(JobActions.userJobs.success(response));
      yield put(AppActions.setUserJobsCachedTimestampRange({
        'from': request.from,
        'to': request.to
      }));
      return;
    }
    const lastRange: IJobListTimestampRange = yield select(getUserJobsRange);

    var range: IJobListTimestampRange = lastRange

    var attempts = 0
    while (attempts < getAttemptsOnEmptyResults()) {
      range = yield call(getPaginationRange, range)

      request['from'] = range['from']
      request['to'] = range['to']

      yield put(JobActions.setUserJobsTimestampRange({
        'from': request['from'],
        'to': request['to']
      }));

      const response: IJobs = yield call(api.getUserJobsUnified, request);

      yield put(JobActions.userJobs.success(response));
      yield put(AppActions.setUserJobsCachedTimestampRange({
        'from': request['from'],
        'to': request['to']
      }));
      if (response.Items.length > 0) {
        break
      }
      attempts += 1
    }
  } catch (error: any) {
    showErrorMessage(error);
    yield put(JobActions.userJobs.failure(error));
  }
}

export function* getOrgJobs(action: ReturnType<typeof JobActions.orgJobs.request>): any {
  try {
    const request = action.payload
    const lastRange: IJobListTimestampRange = yield select(getOrgJobsRange);

    var range: IJobListTimestampRange = lastRange

    var attempts = 0
    while (attempts < getAttemptsOnEmptyResults()) {
      range = yield call(getPaginationRange, range)

      request['from'] = range['from']
      request['to'] = range['to']

      yield put(JobActions.setOrgJobsTimestampRange({
        'from': request['from'],
        'to': request['to']
      }));

      const response: IJobs = yield call(api.getOrgJobs, request);

      yield put(JobActions.orgJobs.success(response));
      yield put(AppActions.setOrgJobsCachedTimestampRange({
        'from': request['from'],
        'to': request['to']
      }));
      if (response.Items.length > 0) {
        break
      }
      attempts += 1
    }
  } catch (error: any) {
    showErrorMessage(error);
    yield put(JobActions.orgJobs.failure(error));
  }
}

export function* jobStatusWatchWorker(action: ReturnType<typeof JobActions.pollJobStatus.request>): any {
  console.log('[POLLING] New poll request for job:', action.payload, 'Canceling any previous poll');
  yield put({ type: CANCEL_STATUS_POLLING })
  // Race starts two concurrent effects. We start our polling effect 'task'. As soon
  // as the take effect 'cancelled' is triggered, the 'task' will be cancelled.
  const result = yield race({
    //4. Start the polling worker
    task: call(jobStatusPollingWorker, action),
    //5. Start a take effect waiting for the cancel action.
    cancel: take(CANCEL_STATUS_POLLING)
  })

  if (result.cancel) {
    console.log('[POLLING] Poll for job', action.payload, 'was cancelled');
  }
}

export function* jobStatusPollingWorker(action: ReturnType<typeof JobActions.pollJobStatus.request>): any {
  const startTime = Date.now();
  let pollCount = 0;
  let lastStatus: JobStatus | undefined = undefined;

  leaveBreadcrumb('Job polling started', {
    jobId: action.payload,
    pollingInterval: getPollingDelay()
  }, 'process');

  while (true) {
    try {
      pollCount++;
      const response: IJob = yield call(api.getJobUnified, action.payload);
      console.log('[POLLING] Poll #' + pollCount + ' for job', action.payload, '- Status:', response.jobStatus);
      yield put(JobActions.pollJobStatus.success(response));

      if (response.processingMode === 'local') {
        console.log('[POLLING] Local job detected, stopping poll');
        yield put({ type: CANCEL_STATUS_POLLING });
        break;
      }

      // Log status transitions
      if (lastStatus !== response.jobStatus) {
        console.log('[POLLING] Status changed from', lastStatus, 'to', response.jobStatus);
        leaveBreadcrumb('Job status changed', {
          jobId: action.payload,
          fromStatus: lastStatus,
          toStatus: response.jobStatus,
          pollCount: pollCount,
          elapsedTime: Date.now() - startTime
        }, 'state');
        lastStatus = response.jobStatus;
      }

      if (response.jobStatus !== JobStatus.JOB_COMPLETED
        && response.jobStatus !== JobStatus.JOB_FAILED
      ) {
        console.log('[POLLING] Job not complete, waiting', getPollingDelay(), 'ms before next poll');
        yield delay(getPollingDelay());
      } else {
        console.log('[POLLING] Job reached terminal status:', response.jobStatus, '- Stopping poll');
        const totalDuration = Date.now() - startTime;

        leaveBreadcrumb('Job completed', {
          jobId: action.payload,
          totalDuration: totalDuration,
          pollCount: pollCount,
          grainType: response.grainId,
          hasResults: !!response.results
        }, 'process');

        addMetadata('currentJob', {
          jobId: action.payload,
          status: response.jobStatus,
          completedAt: new Date().toISOString(),
          processingDuration: totalDuration,
          pollCount: pollCount,
          grainName: response.grainId
        });

        addMetadata('performance', {
          lastJobProcessingTime: totalDuration,
          lastJobPollCount: pollCount
        });

        yield put({ type: CANCEL_STATUS_POLLING })
      }
    } catch (error: any) {
      leaveBreadcrumb('Job polling error', {
        jobId: action.payload,
        pollCount: pollCount,
        error: error.message
      }, 'error');
      showErrorMessage(error, {
        flow: 'job_polling',
        jobId: action.payload,
        pollCount: pollCount
      });
      yield delay(getPollingDelay());
    }
  }
}

export function* userJobsWatchWorker(action: ReturnType<typeof JobActions.pollUserJobs.request>) {
  yield put({ type: CANCEL_USER_JOBS_POLLING })
  // Race starts two concurrent effects. We start our polling effect 'task'. As soon
  // as the take effect 'cancelled' is triggered, the 'task' will be cancelled.
  yield race({
    //4. Start the polling worker
    task: call(userJobsPollingWorker, action),
    //5. Start a take effect waiting for the cancel action.
    cancel: take(CANCEL_USER_JOBS_POLLING)
  })
}

export function* userJobsPollingWorker(action: ReturnType<typeof JobActions.pollUserJobs.request>) {
  while (true) {
    try {
      const request = action.payload
      const cachedRange: IJobListTimestampRange = yield select(getUserJobsCachedRange);

      var range: IJobListTimestampRange = yield call(getPollingRange, cachedRange)
      request['from'] = range['from']
      request['to'] = range['to']

      const response: IJobs = yield call(api.getUserJobsUnified, request);
      yield put(JobActions.pollUserJobs.success(response));
      yield put(AppActions.setUserJobsCachedTimestampRange({
        'from': request['from'],
        'to': request['to']
      }));
      yield delay(getJobListPollingDelay());
    } catch (error: any) {
      showErrorMessage(error);
      yield delay(getJobListPollingDelay());
    }
  }
}
export function* deleteJob(action: ReturnType<typeof JobActions.deleteJob.request>): any {
  try {
    const jobId: string = action.payload;

    leaveBreadcrumb('Job deletion started', {
      jobId: jobId
    }, 'user');

    const response = yield call(api.deleteJobUnified, jobId);

    leaveBreadcrumb('Job deleted successfully', {
      jobId: jobId
    }, 'user');

    yield put(JobActions.deleteJob.success(response));
  } catch (error: any) {
    leaveBreadcrumb('Job deletion failed', {
      jobId: action.payload,
      error: error.message
    }, 'error');
    showErrorMessage(error, {
      flow: 'job_deletion',
      jobId: action.payload
    });
    yield put(JobActions.deleteJob.failure(error));
  }
}


export function* orgJobsPollingWorker(action: ReturnType<typeof JobActions.pollOrgJobs.request>) {
  while (true) {
    try {
      const request = action.payload
      const cachedRange: IJobListTimestampRange = yield select(getUserJobsCachedRange);

      var range: IJobListTimestampRange = yield call(getPollingRange, cachedRange)

      request['from'] = range['from']
      request['to'] = range['to']

      const response: IJobs = yield call(api.getOrgJobs, request);
      yield put(JobActions.pollOrgJobs.success(response));
      yield put(AppActions.setOrgJobsCachedTimestampRange({
        'from': request['from'],
        'to': request['to']
      }));

      yield delay(getJobListPollingDelay());
    } catch (error: any) {
      showErrorMessage(error);
      yield delay(getJobListPollingDelay());
    }
  }
}

/*
 * WATCHERS
 */

export function* watchGetJobPolling() {
  yield takeEvery(JobActions.pollJobStatus.request, jobStatusWatchWorker);
}

export function* watchUserJobsPolling() {
  yield takeEvery(JobActions.pollUserJobs.request, userJobsWatchWorker);
}

export function* watchOrgJobsPolling() {
  yield takeEvery(JobActions.pollOrgJobs.request, orgJobsPollingWorker);
}

export function* watchCreateJob() {
  yield takeEvery(JobActions.createJob.request, createJob);
}
export function* watchQueueJob() {
  yield takeEvery(JobActions.queueJob.request, queueJob);
}

export function* watchUpdateJob() {
  yield takeEvery(JobActions.updateJob.request, updateJob);
}

export function* watchUpdateLocalJobInfo() {
  yield takeEvery(JobActions.updateLocalJobInfo.request, updateLocalJobInfo);
}

export function* watchUpdateJobResult() {
  yield takeEvery(JobActions.updateJobResult.request, updateJobResult);
}

export function* watchProcessLocalJob() {
  yield takeEvery(JobActions.processLocalJob.request, processLocalJob);
}

export function* watchProcessLocalAveragingJob() {
  yield takeEvery(JobActions.processLocalAveragingJob.request, processLocalAveragingJob);
}

export function* watchGetJob() {
  yield takeEvery(JobActions.jobStatus.request, getJob);
}

export function* watchGetUserJobs() {
  yield takeEvery(JobActions.userJobs.request, getJobs);
}

export function* watchGetOrgJobs() {
  yield takeEvery(JobActions.orgJobs.request, getOrgJobs);
}
export function* watchDeleteJob() {
  yield takeEvery(JobActions.deleteJob.request, deleteJob);
}

export default function* root() {
  yield all([fork(watchCreateJob),
  fork(watchQueueJob),
  fork(watchUpdateJob),
  fork(watchUpdateLocalJobInfo),
  fork(watchUpdateJobResult),
  fork(watchProcessLocalJob),
  fork(watchProcessLocalAveragingJob),
  fork(watchGetJob),
  fork(watchGetJobPolling),
  fork(watchGetUserJobs),
  fork(watchUserJobsPolling),
  fork(watchGetOrgJobs),
  fork(watchDeleteJob),
  fork(watchOrgJobsPolling)]);
}
